import React, { useState } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers';
import { RouteComponentProps, Link } from 'react-router-dom';
import { Globals, Constants, Colors, Print, checkPermission } from 'utils';
import { Table, Pagination, Tooltip, Input, DatePicker, Select, Modal } from 'components';
import { MembershipService, PackageService } from 'services';
import type { UserPackage, UserPackageServiceItem } from 'types';
import { BgProfileImage } from 'assets/img';
import { ArrowRight } from 'assets/icons';
import moment from 'moment';
import AddMembership from './add-membership';
import RenewMembership from './renew-membership';

interface Props extends RootState, RouteComponentProps {
  dispatch: any;
}

const INITIAL_STATE = {};

class Memberships extends React.Component<Props> {
  state = {
    header: ['Membresía', 'Nro de Ref', 'Fecha', 'Vence', 'Monto pagado', 'Método de pago'],
    form: INITIAL_STATE,
    data: [],
    page: 1,
    last_page: 1,
    item: null,
    visible: false,
    plan_user: null,
    need_renew: false,
    items: [],
    user_packages: [] as UserPackage[],
  };

  componentDidMount() {
    this.props.dispatch({
      type: 'SET_HEADER',
      payload: {
        title: 'Membresías',
        onBack: null,
      },
    });

    this.props.dispatch({
      type: 'SET_SUBHEADER',
      payload: {
        title: '',
      },
    });

    this.load();
  }

  load = async (withoutLoading: boolean = false) => {
    const [res, packagesRes]: any[] = await Promise.all([
      MembershipService.getUser({
        withoutLoading,
        page: this.state.page,
        ...this.state.form,
        user_id: this.props.user?.id,
      }),
      PackageService.getUserPackages({
        user_id: this.props.user?.id,
        withoutLoading: true,
      }),
    ]);
    this.setState({
      data: res.payments.rows,
      last_page: res.payments.count,
      plan_user: res.plan_user,
      items: res.items || [],
      need_renew: res.need_renew,
      user_packages: packagesRes.user_packages || [],
    });
  };

  reset = () => {
    this.setState(
      {
        form: INITIAL_STATE,
        page: 1,
      },
      () => this.load(true),
    );
  };

  change = (e: any, callback = () => {}) => {
    this.setState(
      {
        page: 1,
        form: {
          ...this.state.form,
          [e.target.name]: e.target.value,
        },
      },
      callback,
    );
  };

  render() {
    const plan_user: any = this.state.plan_user;

    return (
      <div id='client-memberships'>
        <div className='row'>
          <div className='col-lg-8 col-12 order-2 order-lg-1'>
            <Table title='' data={this.state.data.length} header={this.state.header}>
              {this.state.data.map((i: any, index: number) => {
                return (
                  <tr key={index}>
                    <td>{i.membership?.name}</td>
                    <td className='blue'>{i.reference}</td>
                    <td>{moment(i.created_at).format('DD/MM/YYYY')}</td>
                    <td>{moment(i.plan_user?.next_payment).format('DD/MM/YYYY')}</td>
                    <td>{Globals.formatMiles(i.total)}</td>
                    <td>{i?.payment_method?.name}</td>
                  </tr>
                );
              })}
            </Table>

            <Pagination
              pages={this.state.last_page}
              active={this.state.page}
              onChange={async (page: number) => {
                await this.setState({
                  page: page,
                });
                this.load();
              }}
            />
          </div>

          <div className='col-lg-4 col-12 order-1 order-lg-2'>
            <ContainerCardMembership
              user={this.props.user}
              plan_user={plan_user}
              need_renew={this.state.need_renew}
              user_packages={this.state.user_packages}
              load={this.load}
              location={this.props.location}
              history={this.props.history}
            />
          </div>
        </div>
      </div>
    );
  }
}

export default connect((state: RootState) => {
  return {
    user: state.user,
  };
})(Memberships);

const ContainerCardMembership = (params: any) => {
  // Los abogados no pueden pagar ni cambiar su membresía desde esta pantalla.
  const isLawyer = params?.user?.level_id === Constants.LEVELS.LAWYER;
  const [visible, setVisible] = useState(false);
  const [visibleRenew, setVisibleRenew] = useState(false);
  const [membershipId, setMembershipId] = useState(null);
  const [item, setItem] = useState(null);
  // Catálogo de paquetes disponibles solo para saber si mostrar el botón
  // "Paquetes" (la lista completa se carga dentro de AddMembership).
  const [hasPackages, setHasPackages] = useState(false);
  // Si true, al abrir el modal AddMembership se le pide hacer scroll
  // automático hasta la sección "Paquetes".
  const [scrollToPackages, setScrollToPackages] = useState(false);

  React.useEffect(() => {
    let mounted = true;
    PackageService.get({ user_id: params?.user?.id, withoutLoading: true })
      .then((res: any) => {
        if (mounted) setHasPackages((res?.packages || []).length > 0);
      })
      .catch(() => {
        if (mounted) setHasPackages(false);
      });
    return () => {
      mounted = false;
    };
  }, [params?.user?.id]);

  React.useEffect(() => {
    const search = params?.location?.search;
    if (!search) return;
    const query = new URLSearchParams(search);
    if (query.get('pay') === 'true' && !isLawyer) {
      setVisible(true);
      query.delete('pay');
      const newSearch = query.toString();
      params?.history?.replace({
        pathname: params.location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
  }, [params?.location?.search]);

  const openModal = (e: any, membership_id?: any) => {
    e.preventDefault();
    if (membership_id) {
      setMembershipId(membership_id);
      setVisibleRenew(true);
      return;
    }
    setVisible(true);
  };

  const openPackages = (e: any) => {
    e.preventDefault();
    setScrollToPackages(true);
    setVisible(true);
  };

  const closeModal = (
    load: boolean = false,
    backToRenew: boolean = false,
    renewMembershipId?: any,
  ) => {
    setVisible(false);
    setScrollToPackages(false);
    if (!backToRenew) {
      setVisibleRenew(false);
      setMembershipId(null);
    } else {
      // Vino del botón "Pagar" dentro del modal AddMembership
      if (renewMembershipId) {
        setMembershipId(renewMembershipId);
      }
      setVisibleRenew(true);
    }
    if (load) {
      params?.load && params?.load();
    }
  };

  const closeRenewModal = (openMembership: boolean = false, item: any = null) => {
    setItem(null);
    if (openMembership) {
      setVisible(true);
      setItem(item);
    } else {
      setVisibleRenew(false);
    }
  };

  return (
    <>
      {
        <Modal
          className={`modal-container-renew-membership ${visible ? 'hidden' : ''}`}
          visible={visibleRenew}
          hideHeader={true}
          onClose={() => closeRenewModal()}
        >
          {/* @ts-ignore */}
          <RenewMembership onClose={(close: boolean, item: any) => closeRenewModal(close, item)} />
        </Modal>
      }
      {
        <Modal
          className='modal-container-membership'
          visible={visible}
          title=''
          onClose={() => closeModal()}
        >
          {/* @ts-ignore */}
          <AddMembership
            membership_id={membershipId}
            item={item}
            current_plan_user={params.plan_user}
            scrollToPackages={scrollToPackages}
            onClose={(load: boolean, backToRenew: boolean, renewMembershipId?: any) =>
              closeModal(load, backToRenew, renewMembershipId)
            }
          />
        </Modal>
      }

      <div className='container-membership' style={{ backgroundImage: `url(${BgProfileImage})` }}>
        {params?.user ? (
          <p className='subtitle'>Hola {params.user?.name}</p>
        ) : (
          <p className='membership'>Membresía</p>
        )}
        {params.plan_user != null && <p className='title'>{params.plan_user?.membership?.name}</p>}
        <div>
          <div className='container-date'>
            <p>
              {params.plan_user != null
                ? 'Vence ' + moment(params.plan_user?.finish_at).format('DD-MM-YYYY')
                : 'No tiene membresía'}
            </p>
          </div>
        </div>
        {!isLawyer || true && (
          <div className='container-buttons'>
            {params.plan_user != null &&
              params.plan_user?.plan_payment?.mode == Constants.PLAN_PAYMENT_MODES.MONTH && (
                <button
                  className='btn btn-renew'
                  onClick={(e) => openModal(e, params.plan_user?.membership_id)}
                >
                  Pagar
                </button>
              )}
            <button className='btn btn-change' onClick={(e) => openModal(e)}>
              {params.plan_user != null ? 'Cambiar' : 'Adquirir'}
              <img src={ArrowRight} />
            </button>
          </div>
        )}
      </div>

      {/* TODO: botón "Ver paquetes disponibles" oculto temporalmente para todos los usuarios.
      {hasPackages && (
        <div className='container-view-packages'>
          <button className='btn btn-packages' onClick={(e) => openPackages(e)}>
            Ver paquetes disponibles
          </button>
        </div>
      )}
      */}
      {(params.user_packages || []).length > 0 && (
        <div className='container-user-packages'>
          <p className='title-user-packages'>Mis paquetes</p>
          {(params.user_packages as UserPackage[]).map((userPackage, index) => (
            <div className='package-card' key={userPackage.id ?? index}>
              <div className='package-card-header'>
                <p className='package-name'>{userPackage.package?.name}</p>
                {!!userPackage.finish_at && (
                  <p className='package-expiry'>
                    Vence {moment(userPackage.finish_at).format('DD-MM-YYYY')}
                  </p>
                )}
              </div>
              {(userPackage.user_package_services || []).map(
                (service: UserPackageServiceItem, serviceIndex: number) => {
                  // Disponible = cupo total - ya consumidos (no es el total).
                  const available = (service.max_uses ?? 0) - (service.uses ?? 0);
                  return (
                    <div className='item-uses' key={service.service_id ?? serviceIndex}>
                      <div className='col-service'>
                        <p className='service-name'>{service.service?.name}</p>
                      </div>
                      <div className='col-used'>
                        <p className='used'>{service.uses}</p>
                      </div>
                      <div className='col-available'>
                        <span className='available-badge'>
                          {service.unlimited ? '∞' : available > 0 ? available : 0}
                        </span>
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export { ContainerCardMembership };
